import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "store/rootReducer";
import { API_URL } from "config";
import { AdminGalleryInitialState } from "./types";
import { ProductCartItem } from "features/cart/types";
import Axios from "axios";
import userStorage from "store/UserStorage";
import { createImageObjects, transliterate } from "../Products/actions/utils";
import { AdminGalleryItemType } from "features/gallery/types";
import { fetchGalleryItems } from "features/gallery/gallerySlice";
import { batch } from "react-redux";
import { setResponseMessage } from "../adminSlice";

const initialState: AdminGalleryInitialState = {
  adminGalleryItems: [],
  isLoading: false,
  hasError: false,
  projectElements: [],
  elementsLoading: false,
  editItem: [],
};

const adminGallery = createSlice({
  name: "tagsAdminSlice",
  initialState,
  reducers: {
    setAdminGalleryItems(state, action: PayloadAction<AdminGalleryItemType[]>) {
      state.adminGalleryItems = action.payload;
      state.isLoading = false;
    },
    setHasError(state, action: PayloadAction<boolean>) {
      state.hasError = action.payload;
      state.isLoading = false;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setProjectElements(state, action: PayloadAction<ProductCartItem[]>) {
      state.projectElements = action.payload;
      state.elementsLoading = false;
    },
    setElementsLoading(state, action: PayloadAction<boolean>) {
      state.elementsLoading = action.payload;
    },
    setEditItem(state, action) {
      state.editItem = action.payload;
      state.isLoading = false;
    },
  },
});
export const {
  setAdminGalleryItems,
  setIsLoading,
  setHasError,
  setProjectElements,
  setElementsLoading,
  setEditItem,
} = adminGallery.actions;

export const selectAdminGalleryLoading = (state: RootState) => state.adminGallery.isLoading;

export const selectAdminGalleryHasError = (state: RootState) => state.adminGallery.hasError;

export const selectAdminGalleryItems = (state: RootState) => state.adminGallery.adminGalleryItems;
export const selectAdminProjectElements = (state: RootState) => state.adminGallery.projectElements;
export const selectAdminProjectElementsLoading = (state: RootState) => state.adminGallery.elementsLoading;
export const selectEditItem = (state: RootState) => state.adminGallery.editItem;

export const adminFetchAllGalleryItems = () => async (dispatch: any) => {
  dispatch(setIsLoading(true));
  try {
    const result = await axios.get(`${API_URL}/admin/project`);
    dispatch(setAdminGalleryItems(result.data.data));
  } catch (error: any) {
    console.log(`Error in fetchAllTags: ${error.message}`);
  }
};

// TODO: SAME AS in ordersAdminSlice export it
export const fetchProjectElements = (
  items: {
    _id: string;
    id: string;
    count: number;
  }[]
) => async (dispatch: any) => {
  dispatch(setElementsLoading(true));
  try {
    const query = items.map((item) => `ids=${item.id}`);
    const products = await Axios.get(`${API_URL}/shopping-bag-items?${query.join("&")}`);

    const mappedProducts = products.data.data.map((product: any) => {
      return {
        product,
        count: items.find((prod) => prod.id === product.refId)?.count || 0,
      };
    });

    dispatch(setProjectElements(mappedProducts));
  } catch (error: any) {
    dispatch(setHasError(true));
  }
};

export const addProject = (data: any) => async (dispatch: any) => {
  // dispatch(setIsLoading(true));
  try {
    const { images, updatedImages } = createImageObjects(data);
    data.updatedImages = updatedImages;
    data.alias = transliterate(data.title);
    let formData = new FormData();
    formData.append("data", JSON.stringify(data));

    for (var x = 0; x < images.length; x++) {
      formData.append("file", images[x]);
    }
    let token = userStorage.getUser();
    const response = await axios.post(`${API_URL}/admin/project`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно качихте проект от галерията.",
          hasError: false,
        })
      );
      dispatch(fetchGalleryItems());
    });
  } catch (error: any) {
    dispatch(setHasError(true));
  }
};

export const updateGalleryItem = (
  data: any,
  id: string,
  onUploadProgress: (e: ProgressEvent<EventTarget>) => void
) => async (dispatch: any) => {
  try {
    const { images, updatedImages } = createImageObjects(data);
    data.updatedImages = updatedImages;
    data.images = images;
    data.alias = data.alias ?? transliterate(data.title);
    let formData = new FormData();
    formData.append("project", JSON.stringify(data));

    for (var x = 0; x < images.length; x++) {
      formData.append("file", images[x]);
    }
    let token = userStorage.getUser();
    const response = await axios.put(`${API_URL}/admin/project/${id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });

    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно променихте проект от галерията.",
          hasError: false,
        })
      );
      dispatch(fetchGalleryItems());
    });
  } catch (error: any) {
    dispatch(setResponseMessage({ message: error.message, hasError: true }));
  }
};

export const fetchProductForEdit = (id: string) => async (dispatch: any) => {
  dispatch(setIsLoading(true));
  try {
    const response = await axios.get(`${API_URL}/admin/project-values/${id}`);
    dispatch(setEditItem(response.data.data));
  } catch (error: any) {
    dispatch(setHasError(true));
  }
};

export const deleteProduct = (id: string) => async (dispatch: any) => {
  dispatch(setIsLoading(true));
  try {
    let token = userStorage.getUser();
    const response = await axios.delete(`${API_URL}/admin/project/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    batch(() => {
      dispatch(
        setResponseMessage({
          message: "Успешно изтрихте проект от галерията.",
          hasError: false,
        })
      );
      dispatch(setIsLoading(false));
      dispatch(adminFetchAllGalleryItems());
    });
  } catch (error: any) {
    dispatch(setResponseMessage({ message: error.message, hasError: true }));
  }
};

export default adminGallery.reducer;
